/**
 * analytics.js — PostHog, only after the visitor says yes.
 *
 * public/index.html defines window.posthog but never starts it. Nothing is
 * loaded from PostHog, and nothing is tracked, until the visitor taps "Allow"
 * on the analytics banner (components/ConsentBanner.js). "No thanks" or no
 * answer = no PostHog at all. The choice is remembered in this browser and
 * also saved on the account (PUT /users/me/analytics-consent), because the
 * server only forwards brief email opens/clicks for founders who allowed it.
 *
 * Once allowed, events are tied to the founder's account id and carry
 * `day_number` (1 = signup day) — see services/analytics.py for the server side.
 *
 * Scope: the AI daily brief only — brief_viewed, brief_tap (app) and
 * brief_email_opened / brief_email_clicked (server). No automatic page or
 * click tracking for the rest of the app.
 */
import axios from 'axios';
import { API_URL } from '../constants/api';
import tokenStore from './tokenStore';

const STORAGE_KEY = 'fp_analytics_consent';
// Set on Vercel (build time): REACT_APP_POSTHOG_KEY and REACT_APP_POSTHOG_HOST
// (EU cloud: https://eu.i.posthog.com). No key = PostHog never starts.
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let started = false;
let currentUser = null;
const listeners = new Set();

/** 'granted' | 'denied' | null (not asked yet) */
export const getConsent = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
};

export const onConsentChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const ph = () => (typeof window !== 'undefined' ? window.posthog : null);

/** Day 1 = the signup day. */
export const dayNumber = (createdAt) => {
  if (!createdAt) return null;
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return null;
  const utcDay = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.max(1, Math.round((utcDay(new Date()) - utcDay(start)) / 86400000) + 1);
};

/** Start PostHog (if allowed) and tie it to the signed-in founder. Safe to call often. */
export const startAnalytics = (user) => {
  if (user !== undefined) currentUser = user;
  const posthog = ph();
  if (getConsent() !== 'granted' || !posthog || !POSTHOG_KEY) return;
  try {
    if (!started) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        on_xhr_error: () => {},
        request_batching: true,
        capture_failures: false,
        // Daily brief analytics ONLY. PostHog's automatic tracking of every
        // page, click and session is switched off — the only events sent are
        // the brief events below (track()) and the server's brief email events.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        capture_performance: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        rageclick: false,
        advanced_disable_feature_flags: true,
      });
      started = true;
    } else if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
    if (currentUser?.id) {
      // The account id only — no name or email is sent.
      posthog.identify(String(currentUser.id), { signup_date: currentUser.created_at || null });
    }
  } catch (_) { /* analytics must never break the app */ }
};

const saveOnAccount = (granted) => {
  const token = tokenStore.get();
  if (!token) return;
  axios.put(`${API_URL}/api/users/me/analytics-consent`, { granted },
    { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
};

/** Banner / Settings: record the choice, then start or stop tracking. */
export const setConsent = (granted) => {
  try { localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied'); } catch (_) {}
  saveOnAccount(granted);
  if (granted) {
    startAnalytics();
  } else if (started) {
    try { ph().opt_out_capturing(); ph().reset(); } catch (_) {}
  }
  listeners.forEach(fn => { try { fn(granted ? 'granted' : 'denied'); } catch (_) {} });
};

/** After sign-in: copy this browser's earlier choice onto the account (once per session). */
let syncedFor = null;
export const syncConsentToAccount = (user) => {
  const consent = getConsent();
  if (!user?.id || !consent || syncedFor === user.id) return;
  syncedFor = user.id;
  saveOnAccount(consent === 'granted');
};

/** Send one event — silently does nothing without consent. */
export const track = (event, props = {}) => {
  if (getConsent() !== 'granted' || !started) return;
  try {
    ph().capture(event, { ...props, day_number: dayNumber(currentUser?.created_at) });
  } catch (_) {}
};
